import {useEffect, useRef} from 'react';

// default logout time is 4:30 PM Central
export const DEFAULT_LOGOUT_TIME_ZONE = 'America/Chicago';
export const DEFAULT_LOGOUT_HOUR = 16;
export const DEFAULT_LOGOUT_MINUTE = 30;

// check every 30 seconds
export const DEFAULT_LOGOUT_CHECK_INTERVAL_MS = 30 * 1000;

type UseSleepLogoutOptions = {
  enabled?: boolean;
  timeZone?: string;
  targetHour?: number;
  targetMinute?: number;
  checkIntervalMs?: number;
};

type ZonedDateTimeParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
};

const getZonedDateTimeParts = (timestampMs: number, timeZone: string): ZonedDateTimeParts => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    hourCycle: 'h23',
  });

  const parts = formatter.formatToParts(new Date(timestampMs));
  const byType = Object.fromEntries(parts.map(part => [part.type, part.value]));

  return {
    year: Number(byType.year),
    month: Number(byType.month),
    day: Number(byType.day),
    hour: Number(byType.hour),
    minute: Number(byType.minute),
  };
};

const getDayKey = (parts: ZonedDateTimeParts): string => `${parts.year}-${parts.month}-${parts.day}`;
const getMinutesSinceMidnight = (parts: ZonedDateTimeParts): number => (parts.hour * 60) + parts.minute;

/**
 * Hook that automatically logs out the user once per day at a target time in a
 * specific timezone.
 *
 * Defaults to a configured Central time for every user, regardless of local timezone.
 * The options object is intentionally configurable so a user preference can be
 * wired in later without changing the hook internals.
 *
 * @param onLogout - Callback function to execute when logout should occur
 * @param options - Scheduling options
 */
export const useSleepLogout = (
  onLogout: () => void | Promise<void>,
  {
    enabled = true,
    timeZone = DEFAULT_LOGOUT_TIME_ZONE,
    targetHour = DEFAULT_LOGOUT_HOUR,
    targetMinute = DEFAULT_LOGOUT_MINUTE,
    checkIntervalMs = DEFAULT_LOGOUT_CHECK_INTERVAL_MS,
  }: UseSleepLogoutOptions = {},
) => {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isLogoutInProgressRef = useRef(false);
  const lastLogoutDateRef = useRef<string | null>(null);
  const previousCheckRef = useRef<{dayKey: string; minutesSinceMidnight: number} | null>(null);
  const crossedTargetDateRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      previousCheckRef.current = null;
      crossedTargetDateRef.current = null;
      return;
    }

    const triggerLogout = (dayKey: string) => {
      if (isLogoutInProgressRef.current) {
        return;
      }

      isLogoutInProgressRef.current = true;

      void Promise.resolve(onLogout())
        .then(() => {
          // Only mark the day as handled after a successful logout.
          lastLogoutDateRef.current = dayKey;
        })
        .catch(() => {
          // Avoid unhandled promise rejection if logout fails. Keeping
          // lastLogoutDateRef unchanged allows retry on the next interval.
        })
        .finally(() => {
          isLogoutInProgressRef.current = false;
        });
    };

    const checkIfLogoutIsDue = () => {
      const nowZoned = getZonedDateTimeParts(Date.now(), timeZone);
      const dayKey = getDayKey(nowZoned);
      const currentMinutes = getMinutesSinceMidnight(nowZoned);
      const targetMinutes = (targetHour * 60) + targetMinute;

      if (lastLogoutDateRef.current === dayKey) {
        previousCheckRef.current = {dayKey, minutesSinceMidnight: currentMinutes};
        return;
      }

      const previousCheck = previousCheckRef.current;

      // First check while enabled only establishes a baseline. This prevents
      // forcing logout for users who log in after the target time.
      if (!previousCheck) {
        previousCheckRef.current = {dayKey, minutesSinceMidnight: currentMinutes};
        return;
      }

      const crossedTargetThisDay = previousCheck.dayKey === dayKey
        && previousCheck.minutesSinceMidnight < targetMinutes
        && currentMinutes >= targetMinutes;

      if (previousCheck.dayKey !== dayKey) {
        crossedTargetDateRef.current = null;
      }

      if (crossedTargetThisDay) {
        crossedTargetDateRef.current = dayKey;
      }

      previousCheckRef.current = {dayKey, minutesSinceMidnight: currentMinutes};

      const shouldAttemptLogout = crossedTargetDateRef.current === dayKey
        && currentMinutes >= targetMinutes
        && lastLogoutDateRef.current !== dayKey;

      if (!shouldAttemptLogout) {
        return;
      }

      triggerLogout(dayKey);
    };

    // Run once immediately to initialize the baseline check state.
    checkIfLogoutIsDue();
    intervalRef.current = setInterval(() => {
      checkIfLogoutIsDue();
    }, checkIntervalMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, onLogout, timeZone, targetHour, targetMinute, checkIntervalMs]);
};
